// Observe deployments without reloading the running guest/crew session.
export function startReleaseMonitor({ interval = 30000, currentRelease = document.querySelector('meta[name="slx-release-id"]')?.content } = {}) {
  let alive = true, checking = false, notified = null, request = null;
  let socket = null, retryTimer = null, heartbeat = null, retry = 500, socketRelease = null;
  let latest = null, connected = false, verified = false, queued = false, settleTimer = null, fastChecks = 0;
  const publish = () => window.dispatchEvent(new CustomEvent("slx-release-status", { detail: {
    latest, currentRelease, connected, state: latest && currentRelease && latest.releaseID !== currentRelease ? "update" : verified && currentRelease ? "current" : "unknown",
  } }));
  const announce = (registration) => window.dispatchEvent(new CustomEvent("slx-app-update-ready", { detail: { registration } }));
  const check = async () => {
    if (!alive || document.visibilityState === "hidden" || navigator.onLine === false) return;
    if (checking) { queued = true; return; }
    checking = true;
    request = new AbortController();
    const timeout = setTimeout(() => request?.abort(), 10000);
    try {
      let registration;
      try {
        registration = await navigator.serviceWorker?.getRegistration?.();
        if (!alive) return;
        if (registration?.waiting) announce(registration);
        // Also checks backend-only releases, whose worker contains release metadata.
        Promise.resolve(registration?.update?.()).catch(() => {});
      } catch { /* Metadata still works without a service worker. */ }
      if (!alive || !currentRelease) return;
      const response = await fetch(`/slx-version.json?check=${Date.now()}`, { cache: "no-store", signal: request.signal });
      if (!response.ok) { verified = false; publish(); return; }
      const release = await response.json();
      if (alive && typeof release.version === "string" && release.appId === "slx-one" && typeof release.releaseID === "string" && /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/.test(release.releaseID)) {
        latest = { appId: release.appId, releaseID: release.releaseID, version: typeof release.version === "string" ? release.version.slice(0,40) : null };
        verified = true; publish();
        if (currentRelease && release.releaseID !== currentRelease && release.releaseID !== notified) {
          notified = release.releaseID; announce(registration);
        }
      } else if (alive) { verified = false; publish(); }
    } catch { if (alive) { verified = false; publish(); } /* Keep unknown distinct from up-to-date. */ }
    finally {
      clearTimeout(timeout); checking = false;
      if (alive && queued) { queued = false; clearTimeout(settleTimer); settleTimer = setTimeout(check,0); }
      else if (alive && socketRelease && latest?.releaseID !== socketRelease && fastChecks > 0) { fastChecks -= 1; clearTimeout(settleTimer); settleTimer = setTimeout(check,1000); }
    }
  };
  const connect = () => {
    if (!alive || typeof WebSocket === "undefined" || navigator.onLine === false || document.visibilityState === "hidden" || (socket && socket.readyState < 2)) return;
    clearTimeout(retryTimer);
    try {
      const url = new URL("/api/ws/release", process.env.REACT_APP_BACKEND_URL || window.location.origin);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(url.toString());
      const owned = socket;
      owned.onopen = () => {
        if (!alive || socket !== owned) return;
        retry = 500; connected = true; publish();
        clearInterval(heartbeat); heartbeat = setInterval(() => { try { owned.send("ping"); } catch {} },20000);
      };
      owned.onmessage = (event) => {
        if (!alive || socket !== owned) return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === "release" && data.release?.appId === "slx-one" && typeof data.release.releaseID === "string" && data.release.releaseID !== socketRelease) {
            socketRelease = data.release.releaseID; fastChecks = 30;
            // Verify the web release is serving before announcing API startup.
            check();
          }
        } catch {}
      };
      owned.onerror = () => owned.close();
      owned.onclose = () => {
        if (!alive || socket !== owned) return;
        clearInterval(heartbeat); socket = null; socketRelease = null; connected = false; verified = false; publish();
        retryTimer = setTimeout(connect,retry); retry = Math.min(retry*2,10000);
      };
    } catch { socket = null; retryTimer = setTimeout(connect,retry); retry = Math.min(retry*2,10000); }
  };
  const resume = () => { connect(); check(); };
  const offline = () => { verified = false; connected = false; publish(); socket?.close(); };
  connect();
  check();
  const timer = setInterval(check, interval);
  window.addEventListener("online", resume);
  window.addEventListener("offline", offline);
  window.addEventListener("focus", resume);
  window.addEventListener("pageshow", resume);
  document.addEventListener("visibilitychange", resume);
  return () => { alive = false; request?.abort(); clearInterval(timer); clearInterval(heartbeat); clearTimeout(retryTimer); clearTimeout(settleTimer); socket?.close(); window.removeEventListener("online", resume); window.removeEventListener("offline", offline); window.removeEventListener("focus", resume); window.removeEventListener("pageshow", resume); document.removeEventListener("visibilitychange", resume); };
}

export function safeToPromptForUpdate() {
  const active = document.activeElement;
  return navigator.onLine !== false && document.visibilityState !== "hidden" && !document.querySelector('[data-slx-update-busy="true"]') &&
    !active?.matches?.('input, textarea, [contenteditable="true"]');
}
